import React, { useState, useEffect } from "react";
import {
  FiCalendar,
  FiCopy,
  FiCheckCircle,
  FiTag,
  FiInfo,
  FiPercent
} from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../baseurl";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${baseurl}getAllCoupons`);
      if (data.success) {
        setCoupons(data.coupons || []);
      }
    } catch (err) {
      console.error("Error fetching coupons:", err);
      // Fallback to empty array if request fails
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (couponName) => {
    navigator.clipboard.writeText(couponName)
      .then(() => {
        Toast.fire({
          icon: "success",
          title: `Code "${couponName}" copied!`,
        });
      })
      .catch((err) => {
        console.error("Could not copy code: ", err);
        Toast.fire({
          icon: "error",
          title: "Failed to copy code",
        });
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="coupons-page-container">
      {/* Header Section */}
      <div className="coupons-header shadow-sm sticky-top bg-white">
        <div className="container py-3 d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0 d-flex align-items-center gap-2">
            <FiTag className="text-primary" /> Available Coupons ({coupons.length})
          </h5>
          <Link to="/" className="text-primary text-decoration-none fw-medium small">
            Continue Shopping
          </Link>
        </div>
      </div>

      <div className="container py-4">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : coupons.length === 0 ? (
          <div className="empty-coupons text-center py-5 shadow-sm bg-white rounded">
            <img 
              src="https://rukminim1.flixcart.com/www/800/800/promos/16/05/2019/d405a710-21da-4f32-9022-15fbfaae6666.png?q=90" 
              alt="No Coupons Available" 
              style={{ width: '160px' }} 
              className="mb-4 opacity-75" 
            />
            <h5 className="fw-bold">No Coupons Available Right Now!</h5>
            <p className="text-muted">Check back later for exclusive deals and discounts.</p>
            <Link to="/" className="btn btn-primary px-5 py-2 mt-2">Shop Now</Link>
          </div>
        ) : (
          <div className="row g-3">
            {coupons.map((coupon) => {
              const isExpired = new Date(coupon.expiry_date) < new Date();
              const remainingUsage = coupon.usage_limit - coupon.times_used;

              return (
                <div key={coupon.id} className="col-12 col-md-6 col-lg-6">
                  <div className={`coupon-card bg-white shadow-sm rounded p-3 p-md-4 position-relative overflow-hidden h-100 d-flex flex-column justify-content-between ${isExpired ? 'expired-opacity' : ''}`}>
                    
                    {/* Semi-circle punches for authentic ticket cutout aesthetics */}
                    <div className="ticket-punch punch-left"></div>
                    <div className="ticket-punch punch-right"></div>

                    <div>
                      {/* Top Row: Type Badge and Usage Remaining */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className={`badge px-2.5 py-1.5 fs-7 rounded-1 fw-bold ${coupon.coupon_type === 'percentage' ? 'bg-success-light text-success' : 'bg-primary-light text-primary'}`}>
                          {coupon.coupon_type === 'percentage' ? (
                            <><FiPercent className="me-1" /> {Number(coupon.discount_value)}% OFF</>
                          ) : (
                            <><FiTag className="me-1" /> ₹{Number(coupon.discount_value).toLocaleString()} OFF</>
                          )}
                        </span>
                        
                        <span className="text-muted small d-flex align-items-center gap-1">
                          <FiInfo size={14} /> {remainingUsage > 0 ? `${remainingUsage} left` : 'Fully Used'}
                        </span>
                      </div>

                      {/* Coupon Code Presentation Box */}
                      <div className="coupon-code-box d-flex align-items-center justify-content-between p-2 mb-3 bg-light border border-dashed rounded">
                        <code className="coupon-title text-uppercase fw-bold text-dark px-2">
                          {coupon.coupon_name}
                        </code>
                        <button 
                          onClick={() => copyToClipboard(coupon.coupon_name)}
                          className="btn btn-link text-primary text-decoration-none fw-bold small p-1 d-flex align-items-center gap-1"
                          title="Copy Code"
                          disabled={isExpired}
                        >
                          <FiCopy /> <span className="d-none d-sm-inline">COPY</span>
                        </button>
                      </div>

                      {/* Status Check info text */}
                      <p className="text-secondary small mb-3">
                        Use code <strong className="text-dark">{coupon.coupon_name}</strong> to save instantly on eligible purchases. 
                        Valid for {coupon.usage_limit} uses maximum per instance.
                      </p>
                    </div>

                    {/* Bottom Metadata & Expiry tracking */}
                    <div className="border-top pt-2 mt-2">
                      <div className="row align-items-center">
                        <div className="col-12 col-sm-7 mb-2 mb-sm-0">
                          <div className="text-muted fs-7 d-flex align-items-center gap-1">
                            <FiCalendar /> Valid till: <strong>{formatDate(coupon.expiry_date)}</strong>
                          </div>
                        </div>
                        <div className="col-12 col-sm-5 text-sm-end">
                          {coupon.status === "active" && !isExpired ? (
                            <span className="text-success small fw-bold d-inline-flex align-items-center gap-1">
                              <FiCheckCircle /> Active Offer
                            </span>
                          ) : (
                            <span className="text-danger small fw-bold">
                              Expired
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .coupons-page-container { background-color: #f1f3f6; min-height: 100vh; font-family: Roboto, Arial, sans-serif; }
        .coupons-header { border-bottom: 1px solid #e0e0e0; position: sticky; top: 0; z-index: 100; }
        
        /* Premium Coupon Ticket UI Elements */
        .coupon-card { 
          border-left: 5px solid #2874f0; 
          transition: transform 0.2s, box-shadow 0.2s; 
        }
        .coupon-card:hover { 
          transform: translateY(-2px); 
          box-shadow: 0 4px 12px rgba(0,0,0,0.08)!important; 
        }
        
        .coupon-code-box { border-style: dashed !important; border-color: #cbd5e1 !important; }
        .coupon-code-box code { font-size: 16px; letter-spacing: 0.5px; }

        /* Punch cutouts for realistic coupons style architecture */
        .ticket-punch {
          background-color: #f1f3f6;
          position: absolute;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          top: 50%;
          transform: translateY(-50%);
          z-index: 5;
        }
        .punch-left { left: -7px; box-shadow: inset -2px 0px 3px rgba(0,0,0,0.04); }
        .punch-right { right: -7px; box-shadow: inset 2px 0px 3px rgba(0,0,0,0.04); }

        /* Custom Color Variant Adjustments */
        .bg-success-light { background-color: #e8f5e9; }
        .bg-primary-light { background-color: #e3f2fd; }
        .fs-7 { font-size: 0.8rem; }
        .expired-opacity { opacity: 0.65; pointer-events: none; border-left-color: #9e9e9e; }
        
        .btn-primary { background-color: #fb641b; border: none; border-radius: 2px; }
        .btn-primary:hover { background-color: #f4511e; }
        .btn-link { font-size: 13px; }

        @media (max-width: 576px) {
          .coupon-card { padding: 15px!important; }
          .coupon-code-box code { font-size: 14px; }
        }
      `}</style>
    </div>
  );
}

export default Coupons;